import { Injectable, inject } from '@angular/core';
import { CanActivateChildFn, Router } from '@angular/router';
import { Observable, catchError, map, of, shareReplay, switchMap, take } from 'rxjs';
import { AdminApiService } from '../services/admin-api.service';
import { AdminAccessService } from './admin-access.service';

type Pending = Array<{ orgId: string; orgName: string }>;

/** Organizations the signed-in Owner still has to sign an MSA for. Cached per session. */
@Injectable({ providedIn: 'root' })
export class MsaGateService {
  private api = inject(AdminApiService);
  private cache$: Observable<Pending> | null = null;

  pending(): Observable<Pending> {
    this.cache$ ??= this.api.pendingMsa().pipe(
      map((r) => r.pending),
      catchError(() => of([] as Pending)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    return this.cache$;
  }

  /** After signing (or a sign-out), ask again next time. */
  reset(): void {
    this.cache$ = null;
  }
}

/**
 * An Owner cannot use the admin until their organization's MSA is signed
 * (owner decision 2026-09-28). VentureOff staff are never gated, and the
 * signing page itself, sign-out and access pages stay reachable.
 */
export const msaGate: CanActivateChildFn = (_route, state) => {
  const router = inject(Router);
  const gate = inject(MsaGateService);
  const access = inject(AdminAccessService);
  const url = state.url.split('?')[0] ?? '';
  if (/\/sign-msa$/.test(url) || url.startsWith('/my-access')) return true;
  return access.access$.pipe(
    take(1),
    catchError(() => of(null)),
    switchMap((a) => (a?.staff.any ? of([] as Pending) : gate.pending())),
    map((pending) => (pending.length ? router.createUrlTree(['/organizations', pending[0]!.orgId, 'sign-msa']) : true)),
  );
};
